import React, { useEffect, useMemo, useState } from 'react'
import { AccountSnapshot, chooseSnapshot, snapshotHealthy, observationMs, SNAPSHOT_HEALTH_TICK_MS } from '../utils/accountSnapshot'
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Tag,
  Select,
  DatePicker,
  Space,
  Button,
  Spin,
  Empty,
  Alert,
  Tabs,
  Popconfirm,
  message,
} from 'antd'
import {
  DollarOutlined,
  RiseOutlined,
  FallOutlined,
  PercentageOutlined,
  SwapOutlined,
  ReloadOutlined,
  FundOutlined,
  TagsOutlined,
} from '@ant-design/icons'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import ReactECharts from 'echarts-for-react'
import type { EChartsOption } from 'echarts'
import dayjs from 'dayjs'
import type { ColumnsType } from 'antd/es/table'
import { useIsMobile } from '@hooks/useBreakpoint'
import { useCfdLiveSocket } from '@hooks/useCfdLiveSocket'
const { RangePicker } = DatePicker
const { Option } = Select

interface LivePosition {
  position_id: string
  symbol: string
  side: string
  volume: number
  entry_price: number
  current_price?: number | null
  unrealized_pnl?: number | null
  margin?: number | null
  sl?: number | null
  tp?: number | null
  desired_sl?: number | null
  desired_tp?: number[]
  protection_source?: string
  tp_prices?: number[]
  current_tp_index?: number
  comment?: string | null
  signal_id?: string | null
  request_id?: string | null
}

interface LiveOrder {
  order_id: string
  symbol: string
  side: string
  price_type?: string
  state_desc?: string
  volume?: string | number
  price?: string | number
  price_tp?: string | number | null
  price_sl?: string | number | null
  time_setup?: number | null
  comment?: string | null
  signal_id?: string | null
  request_id?: string | null
}

interface CfdLive extends AccountSnapshot {
  token_id: number
  positions: LivePosition[]
  orders: LiveOrder[]
}

interface CfdOverview {
  token_id: number
  days: number
  account: {
    balance: number
    equity: number
    margin: number
    margin_free: number
    unrealized_pnl: number
    open_positions: number
  }
  summary: {
    total_trades: number
    win_rate: number
    total_pnl: number
    total_fee: number
    total_swap: number
    total_net: number
    best_trade: number
    worst_trade: number
  }
  per_symbol: Array<{
    symbol: string
    trades: number
    win_rate: number
    pnl: number
    fee: number
    net: number
  }>
  comment_stats: Array<{
    comment: string
    trades: number
    win_rate: number
    pnl: number
    net: number
  }>
  pnl_curve: Array<{ date: string; pnl: number }>
}

interface PositionRecord {
  position_id: string
  symbol: string
  side: string
  volume: number
  price_open: number
  close_price: number
  realized_pnl: number
  fee: number
  swap: number
  net_pnl: number
  comment?: string | null
  signal_id?: string | null
  open_time_ms?: number | null
  close_time_ms?: number | null
  holding_hours?: number | null
  position_status?: string
}

const fmtMoney = (v: unknown, digits = 2): string => {
  const n = Number(v || 0)
  return Number.isFinite(n) ? n.toFixed(digits) : '-'
}

const fmtTime = (ms?: number | null): string => {
  if (!ms) return '-'
  return dayjs(ms).format('YYYY-MM-DD HH:mm')
}

const pnlColor = (v: unknown): string => {
  const n = Number(v || 0)
  if (n > 0) return '#3f8600'
  if (n < 0) return '#cf1322'
  return '#888'
}

const pnlTag = (v: unknown): React.ReactNode => {
  const n = Number(v || 0)
  if (n > 0) return <Tag color="green">+{n.toFixed(2)}</Tag>
  if (n < 0) return <Tag color="red">{n.toFixed(2)}</Tag>
  return <Tag>0.00</Tag>
}

const CfdStats: React.FC = () => {
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const queryClient = useQueryClient()
  // 时间范围：null=近30天；[startMs, endMs]=任意范围
  const [dateRange, setDateRange] = useState<[number, number] | null>(null)
  const [comment, setComment] = useState<string | undefined>()
  const [symbol, setSymbol] = useState<string | undefined>()
  const [side, setSide] = useState<string | undefined>()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)

  // WebSocket 实时推送状态：连接成功则停轮询；断开自动降级 8s 轮询
  const [wsConnected, setWsConnected] = useState(false)
  useCfdLiveSocket({
    onSnapshot: (data) => {
      queryClient.setQueryData<CfdLive>(['cfd-stats', 'live'], old => chooseSnapshot(old, data as CfdLive))
    },
    onStatusChange: setWsConnected,
    onAccountEvents: () => { void queryClient.invalidateQueries({queryKey: ['cfd-stats']}) },
  })

  const rangeParams = (): Record<string, unknown> => {
    if (dateRange) {
      return { start_time: dateRange[0], end_time: dateRange[1] }
    }
    return { days: 30 }
  }
  const rangeKey = dateRange
    ? `${dateRange[0]}-${dateRange[1]}`
    : 'days-30'

  // 实时持仓 + 挂单（WS 推送；断开时降级 8s 轮询）
  const liveQuery = useQuery({
    queryKey: ['cfd-stats', 'live'],
    queryFn: async () => {
      const res = await fetch('/api/v1/cfd/stats/live')
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const json = await res.json()
      return (json?.data ?? json) as CfdLive
    },
    structuralSharing: (old, next) => chooseSnapshot(old as CfdLive | undefined, next as CfdLive),
    refetchInterval: (query) => wsConnected ? 30000 : Math.min(5000 * 2 ** (query.state.fetchFailureCount || 0), 30000),
  })
  const live = liveQuery.data
  const [now, setNow] = useState(Date.now())
  useEffect(() => {
    const updateClock = () => setNow(Date.now())
    const timer = window.setInterval(updateClock, SNAPSHOT_HEALTH_TICK_MS)
    document.addEventListener('visibilitychange', updateClock)
    window.addEventListener('focus', updateClock)
    return () => {
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', updateClock)
      window.removeEventListener('focus', updateClock)
    }
  }, [])
  const dataFresh = snapshotHealthy(live, now)

  const livePositionColumns: ColumnsType<LivePosition> = [
    { title: '持仓ID', dataIndex: 'position_id', width: 100 },
    { title: '品种', dataIndex: 'symbol', width: 90 },
    {
      title: '方向', dataIndex: 'side', width: 70,
      render: (v) => <Tag color={v === 'long' ? 'green' : 'red'}>{v === 'long' ? '多' : '空'}</Tag>,
    },
    { title: '手数', dataIndex: 'volume', width: 70 },
    { title: '保护观察', key: 'protection', width: 190, render: (_, p) => (
      <span>SL {p.sl ?? '未知'} / TP {p.tp ?? '未知'}<br />
        <Tag color={p.protection_source === 'broker_readback' ? 'blue' : 'orange'}>
          {p.protection_source === 'broker_readback' ? '券商观察值' : '等待独立核验'}
        </Tag>
        <small>目标 SL {p.desired_sl ?? '—'} / TP {(p.desired_tp ?? []).join(', ') || '—'}</small>
      </span>
    ) },
    { title: '开仓价', dataIndex: 'entry_price', width: 90 },
    { title: '浮动盈亏', dataIndex: 'unrealized_pnl', width: 95, render: (v) => (
      <span style={{ color: Number(v) >= 0 ? '#3f8600' : '#cf1322' }}>{Number(v ?? 0).toFixed(2)}</span>
    ) },
    {
      title: 'TP档位', width: 110,
      render: (_, r) => r.tp_prices?.length ? `${r.current_tp_index ?? 0}/${r.tp_prices.length}` : '-',
    },
    { title: '频道', dataIndex: 'comment', width: 140, render: (v) => v || '-' },
  ]

  const liveOrderColumns: ColumnsType<LiveOrder> = [
    { title: '订单ID', dataIndex: 'order_id', width: 100 },
    { title: '品种', dataIndex: 'symbol', width: 90 },
    {
      title: '方向', dataIndex: 'side', width: 70,
      render: (v) => <Tag color={v === 'buy' ? 'green' : 'red'}>{v === 'buy' ? '买' : '卖'}</Tag>
    },
    { title: '类型', dataIndex: 'price_type', width: 80, render: (v) => (v === 'trigger' ? '触发' : v || '-') },
    { title: '价格', dataIndex: 'price', width: 90 },
    { title: '手数', dataIndex: 'volume', width: 70 },
    { title: 'TP', dataIndex: 'price_tp', width: 85, render: (v) => (v && Number(v) > 0 ? String(v) : '-') },
    { title: 'SL', dataIndex: 'price_sl', width: 85, render: (v) => (v && Number(v) > 0 ? String(v) : '-') },
    { title: '频道', dataIndex: 'comment', width: 140, render: (v) => v || '-' },
    {
      title: '操作', width: 80,
      render: () => <Button size="small" onClick={() => navigate(live?.token_id ? `/cfd/accounts/${live.token_id}` : '/cfd')}>到工作区维护</Button>,
    },
  ]

  // 总览（直接 fetch，接口无鉴权；不依赖 api 对象避免模块初始化问题）
  const overviewQuery = useQuery({
    queryKey: ['cfd-stats', 'overview', rangeKey],
    queryFn: async () => {
      const params = new URLSearchParams()
      const rp = rangeParams() as Record<string, unknown>
      Object.entries(rp).forEach(([k, v]) => {
        if (v !== undefined && v !== null) params.set(k, String(v))
      })
      const res = await fetch(`/api/v1/cfd/stats/overview?${params.toString()}`)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const json = await res.json()
      return json?.data ?? json
    },
    refetchInterval: 60000,
  })
  const overview = overviewQuery.data as CfdOverview | undefined

  // comment 列表（跟随时间范围）
  const commentsQuery = useQuery({
    queryKey: ['cfd-stats', 'comments', rangeKey],
    queryFn: async () => {
      const params = new URLSearchParams()
      const rp = rangeParams() as Record<string, unknown>
      Object.entries(rp).forEach(([k, v]) => {
        if (v !== undefined && v !== null) params.set(k, String(v))
      })
      const res = await fetch(`/api/v1/cfd/stats/comments?${params.toString()}`)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const json = await res.json()
      return json?.data ?? json
    },
  })
  const commentList = (commentsQuery.data?.comments ?? []) as string[]

  // 明细
  const positionsQuery = useQuery({
    queryKey: ['cfd-stats', 'positions', rangeKey, comment, symbol, side, page, pageSize],
    queryFn: async () => {
      const params = new URLSearchParams()
      const rp = rangeParams() as Record<string, unknown>
      Object.entries(rp).forEach(([k, v]) => {
        if (v !== undefined && v !== null) params.set(k, String(v))
      })
      ;['comment', 'symbol', 'side', 'page', 'page_size'].forEach((k) => {
        const v = (k === 'page' ? page : k === 'page_size' ? pageSize : k === 'comment' ? comment : k === 'symbol' ? symbol : side) as unknown
        if (v !== undefined && v !== null && v !== '') params.set(k, String(v))
      })
      const res = await fetch(`/api/v1/cfd/stats/positions?${params.toString()}`)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const json = await res.json()
      return json?.data ?? json
    },
  })
  const positionsData = positionsQuery.data as {
    total?: number
    records?: PositionRecord[]
  }

  // 盈亏曲线
  const curveOption = useMemo<EChartsOption>(() => {
    const points = overview?.pnl_curve ?? []
    return {
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const p = params?.[0]
          if (!p) return ''
          return `${p.axisValue}<br/>盈亏: <b>${Number(p.data).toFixed(2)}</b>`
        },
      },
      grid: { left: 50, right: 20, top: 20, bottom: 40 },
      xAxis: {
        type: 'category',
        data: points.map((p) => p.date),
        axisLabel: { fontSize: 10 },
      },
      yAxis: {
        type: 'value',
        axisLabel: { fontSize: 10 },
      },
      series: [
        {
          name: '日盈亏',
          type: 'line',
          smooth: true,
          data: points.map((p) => p.pnl),
          areaStyle: { opacity: 0.15 },
          lineStyle: { width: 2 },
        },
      ],
      color: ['#1677ff'],
    }
  }, [overview])

  // 品种分布
  const symbolOption = useMemo<EChartsOption>(() => {
    const items = overview?.per_symbol ?? []
    return {
      tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
      series: [
        {
          type: 'pie',
          radius: ['35%', '65%'],
          data: items.map((s) => ({ name: s.symbol, value: Math.abs(s.net) })),
          label: { fontSize: 10 },
        },
      ],
    }
  }, [overview])

  const columns: ColumnsType<PositionRecord> = [
    {
      title: '时间',
      dataIndex: 'close_time_ms',
      render: (v, r) => (
        <span>
          <div style={{ fontSize: 12 }}>{fmtTime(r.close_time_ms)}</div>
          <div style={{ fontSize: 11, color: '#999' }}>持 {r.holding_hours ?? '-'}h</div>
        </span>
      ),
      width: 120,
    },
    { title: '品种', dataIndex: 'symbol', width: 80 },
    {
      title: '方向',
      dataIndex: 'side',
      width: 60,
      render: (v) =>
        v === 'long' ? <Tag color="green">多</Tag> : <Tag color="red">空</Tag>,
    },
    { title: '手数', dataIndex: 'volume', width: 60, render: (v) => Number(v || 0).toFixed(2) },
    {
      title: '开仓价',
      dataIndex: 'price_open',
      width: 90,
      render: (v) => Number(v || 0).toFixed(2),
    },
    {
      title: '平仓价',
      dataIndex: 'close_price',
      width: 90,
      render: (v) => (v ? Number(v).toFixed(2) : '-'),
    },
    {
      title: '交易盈亏',
      dataIndex: 'realized_pnl',
      width: 90,
      render: (v) => <span style={{ color: pnlColor(v) }}>{fmtMoney(v)}</span>,
    },
    {
      title: '手续费',
      dataIndex: 'fee',
      width: 70,
      render: (v) => <span style={{ color: '#888' }}>{fmtMoney(Math.abs(v || 0))}</span>,
    },
    {
      title: '利息',
      dataIndex: 'swap',
      width: 60,
      render: (v) => <span style={{ color: '#888' }}>{fmtMoney(v)}</span>,
    },
    {
      title: '净盈亏',
      dataIndex: 'net_pnl',
      width: 100,
      render: (v) => <b style={{ color: pnlColor(v) }}>{fmtMoney(v)}</b>,
    },
    {
      title: '来源',
      dataIndex: 'comment',
      render: (v) => (v ? <Tag color="blue">{v}</Tag> : <span style={{ color: '#bbb' }}>未标注</span>),
    },
  ]

  const queryError =
    overviewQuery.error || commentsQuery.error || positionsQuery.error
  const errMsg = (() => {
    if (!queryError) return null
    const e = queryError as any
    return e?.response?.data?.detail || e?.message || String(e)
  })()

  return (
    <div style={{ padding: 16 }}>
      {errMsg && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="数据加载失败"
          description={`${errMsg}（token: ${localStorage.getItem('panel_token') ? 'panel' : 'none'}）`}
        />
      )}
    <div style={{ padding: isMobile ? 12 : 20 }}>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }} wrap>
        <Space wrap>
          <RangePicker
            allowClear
            value={
              dateRange
                ? [dayjs(dateRange[0]), dayjs(dateRange[1])]
                : null
            }
            onChange={(vals) => {
              if (vals && vals[0] && vals[1]) {
                setDateRange([vals[0].valueOf(), vals[1].valueOf()])
              } else {
                setDateRange(null)
              }
              setPage(1)
            }}
            placeholder={['开始日期', '结束日期']}
            style={{ width: isMobile ? 220 : 260 }}
          />
          <Button
            size="middle"
            onClick={() => {
              setDateRange(null)
              setPage(1)
            }}
          >
            近 30 天
          </Button>
          <Select
            allowClear
            placeholder="来源 comment"
            value={comment}
            onChange={(v) => {
              setComment(v)
              setPage(1)
            }}
            style={{ width: 180 }}
          >
            {commentList.map((c) => (
              <Option key={c} value={c}>
                {c}
              </Option>
            ))}
          </Select>
          <Select
            allowClear
            placeholder="品种"
            value={symbol}
            onChange={(v) => {
              setSymbol(v)
              setPage(1)
            }}
            style={{ width: 120 }}
          >
            {(overview?.per_symbol ?? []).map((s) => (
              <Option key={s.symbol} value={s.symbol}>
                {s.symbol}
              </Option>
            ))}
          </Select>
          <Select
            allowClear
            placeholder="方向"
            value={side}
            onChange={(v) => {
              setSide(v)
              setPage(1)
            }}
            style={{ width: 100 }}
          >
            <Option value="long">做多</Option>
            <Option value="short">做空</Option>
          </Select>
          <Button icon={<ReloadOutlined />} onClick={() => overviewQuery.refetch()}>
            刷新
          </Button>
        </Space>
      </Space>

      <Spin spinning={overviewQuery.isFetching}>
        {/* 实时持仓 + 挂单 */}
        <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
          <Col xs={24} lg={14}>
            <Card
              size="small"
              title={
                <Space size={8}>
                  当前持仓 ({live?.positions?.length ?? 0})
                  <small>{observationMs(live?.broker_observed_at) ? `观察于 ${new Date(observationMs(live?.broker_observed_at)).toLocaleTimeString()}` : '观察时间未知'}</small>
                  <Tag color={dataFresh ? 'green' : 'orange'} style={{ margin: 0 }}>
                    {dataFresh ? (wsConnected ? '实时' : '对账') : '数据陈旧 / 待同步'}
                  </Tag>
                </Space>
              }
              extra={liveQuery.isFetching ? <Spin size="small" /> : undefined}
            >
              <Table<LivePosition>
                rowKey="position_id"
                size="small"
                loading={liveQuery.isLoading}
                dataSource={live?.positions ?? []}
                columns={livePositionColumns}
                pagination={false}
                scroll={{ x: 700 }}
                locale={{ emptyText: live?.positions_complete === true ? '暂无持仓' : '持仓状态暂不可确认' }}
              />
            </Card>
          </Col>
          <Col xs={24} lg={10}>
            <Card size="small" title={`当前挂单 (${live?.orders?.length ?? 0})`}>
              <Table<LiveOrder>
                rowKey="order_id"
                size="small"
                loading={liveQuery.isLoading}
                dataSource={live?.orders ?? []}
                columns={liveOrderColumns}
                pagination={false}
                scroll={{ x: 760 }}
                locale={{ emptyText: live?.orders_complete === true ? '暂无挂单' : '挂单状态暂不可确认' }}
              />
            </Card>
          </Col>
        </Row>

        {/* 统计卡片 */}
        <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
          <Col xs={12} sm={8} md={4}>
            <Card size="small">
              <Statistic
                title="账户余额"
                value={overview?.account?.balance ?? 0}
                precision={2}
                prefix={<DollarOutlined />}
                suffix="USDT"
              />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Card size="small">
              <Statistic
                title="可用保证金"
                value={overview?.account?.margin_free ?? 0}
                precision={2}
                prefix={<SwapOutlined />}
                suffix="USDT"
              />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Card size="small">
              <Statistic
                title="浮动盈亏"
                value={overview?.account?.unrealized_pnl ?? 0}
                precision={2}
                valueStyle={{ color: pnlColor(overview?.account?.unrealized_pnl) }}
                suffix={`/ ${overview?.account?.open_positions ?? 0}仓`}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Card size="small">
              <Statistic
                title="已实现盈亏"
                value={overview?.summary?.total_pnl ?? 0}
                precision={2}
                valueStyle={{ color: pnlColor(overview?.summary?.total_pnl) }}
                prefix={<RiseOutlined />}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Card size="small">
              <Statistic
                title="净盈亏(扣费)"
                value={overview?.summary?.total_net ?? 0}
                precision={2}
                valueStyle={{ color: pnlColor(overview?.summary?.total_net) }}
                prefix={<FundOutlined />}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Card size="small">
              <Statistic
                title={`胜率 (${overview?.summary?.total_trades ?? 0}笔)`}
                value={overview?.summary?.win_rate ?? 0}
                precision={2}
                suffix="%"
                prefix={<PercentageOutlined />}
              />
            </Card>
          </Col>
        </Row>

        {/* 第二行：费用 + 明细卡 */}
        <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
          <Col xs={12} sm={8} md={4}>
            <Card size="small">
              <Statistic
                title="总手续费"
                value={overview?.summary?.total_fee ?? 0}
                precision={2}
                valueStyle={{ color: '#888' }}
                prefix={<FallOutlined />}
                suffix="USDT"
              />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Card size="small">
              <Statistic
                title="隔夜利息"
                value={overview?.summary?.total_swap ?? 0}
                precision={2}
                valueStyle={{ color: pnlColor(overview?.summary?.total_swap) }}
                prefix={<SwapOutlined />}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Card size="small">
              <Statistic
                title="最佳单笔"
                value={overview?.summary?.best_trade ?? 0}
                precision={2}
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Card size="small">
              <Statistic
                title="最差单笔"
                value={overview?.summary?.worst_trade ?? 0}
                precision={2}
                valueStyle={{ color: '#cf1322' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8} md={8}>
            <Card size="small">
              <Statistic
                title="持仓中"
                value={overview?.account?.open_positions ?? 0}
                suffix="笔"
              />
            </Card>
          </Col>
        </Row>
      </Spin>

      {/* 图表区 */}
      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={24} md={16}>
          <Card size="small" title="日盈亏曲线" style={{ height: '100%' }}>
            {overview?.pnl_curve?.length ? (
              <ReactECharts
                option={curveOption}
                style={{ height: isMobile ? 220 : 300, width: '100%' }}
                notMerge
                lazyUpdate
              />
            ) : (
              <Empty description="暂无盈亏数据" style={{ padding: 40 }} />
            )}
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card size="small" title="品种分布" style={{ height: '100%' }}>
            {overview?.per_symbol?.length ? (
              <ReactECharts
                option={symbolOption}
                style={{ height: isMobile ? 220 : 300, width: '100%' }}
                notMerge
                lazyUpdate
              />
            ) : (
              <Empty description="暂无品种数据" style={{ padding: 40 }} />
            )}
          </Card>
        </Col>
      </Row>

      {/* 来源分类统计 */}
      <Card size="small" style={{ marginBottom: 12 }} title={<span><TagsOutlined /> 信号来源统计</span>}>
        {overview?.comment_stats?.length ? (
          <Table
            size="small"
            rowKey="comment"
            pagination={false}
            dataSource={overview.comment_stats}
            columns={[
              { title: '来源 comment', dataIndex: 'comment' },
              { title: '笔数', dataIndex: 'trades', width: 80 },
              {
                title: '胜率',
                dataIndex: 'win_rate',
                width: 90,
                render: (v) => `${v}%`,
              },
              {
                title: '盈亏',
                dataIndex: 'pnl',
                width: 100,
                render: (v) => <span style={{ color: pnlColor(v) }}>{fmtMoney(v)}</span>,
              },
              {
                title: '净盈亏',
                dataIndex: 'net',
                width: 100,
                render: (v) => <b style={{ color: pnlColor(v) }}>{fmtMoney(v)}</b>,
              },
            ]}
          />
        ) : (
          <Empty description="暂无来源数据" />
        )}
      </Card>

      {/* 明细表 */}
      <Card size="small" title="已平仓明细">
        <Table
          size="small"
          rowKey="position_id"
          loading={positionsQuery.isFetching}
          columns={columns}
          dataSource={positionsData?.records ?? []}
          scroll={{ x: 900 }}
          pagination={{
            current: page,
            pageSize,
            total: positionsData?.total ?? 0,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            showTotal: (t) => `共 ${t} 笔`,
            onChange: (p, ps) => {
              setPage(p)
              setPageSize(ps)
            },
          }}
        />
      </Card>
    </div>
    </div>
  )
}

export default CfdStats
